import React from 'react';
import { Card } from './ui/card';
import { dayModes } from '../utils/dayModes';
import { setDayMode } from '../utils/localStorage';
import { Check } from 'lucide-react';

const DayModeCard = ({ mode, isSelected, onSelect }) => {
  return (
    <Card
      data-testid={`day-mode-card-${mode.id}`}
      onClick={() => onSelect(mode.id)}
      className={`
        day-mode-card cursor-pointer p-6 border-2 transition-all duration-300 recipe-card relative overflow-hidden
        ${mode.bgColor} ${isSelected ? 'border-stone-400 shadow-lifted-lg' : 'border-stone-200 hover:border-stone-300'}
      `}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{mode.emoji}</span>
          <div>
            <h3 className={`font-serif text-xl ${isSelected ? mode.textColor : 'text-ink-900'}`}>
              {mode.name}
            </h3>
            <p className="text-sm text-ink-500 font-accent italic">{mode.tagline}</p>
          </div>
        </div>
        {isSelected && (
          <div className={`w-6 h-6 rounded-full ${mode.bgColor} ${mode.borderColor} border-2 flex items-center justify-center`}>
            <Check className={`w-4 h-4 ${mode.textColor}`} />
          </div>
        )}
      </div>
      
      <p className={`text-sm mb-4 ${isSelected ? mode.textColor : 'text-ink-700'}`}>
        {mode.description}
      </p>
      
      {/* Task preview */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-ink-500 font-medium font-accent">
          Today&apos;s focus ({mode.tasks.filter(t => !t.skippable).length} essentials)
        </p>
        <ul className="space-y-1">
          {mode.tasks.slice(0, 3).map((task, idx) => (
            <li 
              key={idx} 
              className={`text-sm flex items-center gap-2 ${isSelected ? mode.textColor : 'text-ink-900'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${task.skippable ? 'bg-stone-400' : isSelected ? mode.borderColor : 'bg-brown'}`} />
              {task.text}
              {task.skippable && <span className="text-xs text-ink-500 font-accent italic">(can skip)</span>}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

const DayModeSelector = ({ currentMode, onModeSelect }) => {
  const handleSelect = (modeId) => {
    setDayMode(modeId);
    onModeSelect(modeId);
  };
  
  return (
    <div className="space-y-6" data-testid="day-mode-selector">
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
          How&apos;s today feeling?
        </h2>
        <p className="text-ink-700 font-accent italic">
          You can change this anytime. It is not permanent. 🌿
        </p>
      </div>
      
      <div className="grid gap-4 md:grid-cols-3">
        {Object.values(dayModes).map((mode) => (
          <DayModeCard
            key={mode.id}
            mode={mode}
            isSelected={currentMode === mode.id}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
};

export default DayModeSelector;
